// ✅ src/pages/VehicleV2/TransferredVehicleStatus.jsx  (UPDATED FULL FILE)
// Mount backend:
//   app.use("/api/vehicleTransferStatus", vehicleTransferStatus);
//
// Backend endpoints used:
//   GET  /api/vehicleTransferStatus/init?branchId=&branchType=&loginUserId=
//   GET  /api/vehicleTransferStatus/list/own?...filters...
//   GET  /api/vehicleTransferStatus/list/spares?...filters...
//
// ✅ Updated pattern:
// - common localStorage / sessionStorage header pattern
// - useMemo + useCallback pattern
// - pageLoading + dropdownLoading + gridLoading states
// - safer init / refresh / filter / list flow
// - fixed stale-state issue in View All
// - avoids unnecessary repeated API calls

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaSearch,
  FaEye,
  FaFilter,
  FaArrowLeft,
  FaSyncAlt,
  FaUndo,
} from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/vehicleTransferStatus";

/* ---------------- helpers ---------------- */
const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v).trim();
};

const toList = (arr) => (Array.isArray(arr) ? arr : []);

const clean = (v) => String(v ?? "").trim();

const selectValue = (v) => {
  const s = clean(v);
  return s ? s : "Select";
};

const MONTHS = [
  "Select",
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const defaultFilters = {
  vehicleMode: "own", // own | spares
  vehicleCode: "",
  vehicleNumber: "",
  transferTo: "Select",
  store: "Select",
  vehTypeIndex: "Select",
  month: "Select",
  year: "Select",
};

const TransferredVehicleStatus = () => {
  const navigate = useNavigate();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  /* ---------------- shared local/session pattern ---------------- */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "0";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  /* ---------------- state ---------------- */
  const [showFilters, setShowFilters] = useState(true);

  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [gridLoading, setGridLoading] = useState(false);

  const [filters, setFilters] = useState(defaultFilters);

  const [opts, setOpts] = useState({
    branches: [{ label: "Select", value: "Select" }],
    stores: [{ label: "Select", value: "Select" }],
    vehicleTypes: [{ label: "Select", value: "Select" }],
    months: MONTHS.map((m) => ({ label: m, value: m })),
    years: [{ label: "Select", value: "Select" }],
  });

  const [transferFromName, setTransferFromName] = useState("");
  const [rows, setRows] = useState([]);

  const [page, setPage] = useState(1);
  const pageSize = 10;

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(rows.length / pageSize)),
    [rows.length]
  );

  const paginatedRows = useMemo(
    () => rows.slice((page - 1) * pageSize, page * pageSize),
    [rows, page]
  );

  const isBusy = pageLoading || dropdownLoading || gridLoading;

  /* ---------------- styles ---------------- */
  const cardClass = useMemo(
    () =>
      `p-6 mt-6 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : `bg-white ${currentSkin.glass} text-black`
      }`,
    [isDark, currentSkin]
  );

  const sectionClass = useMemo(
    () =>
      `rounded-xl p-6 border ${
        isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
      }`,
    [isDark]
  );

  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
      }`,
    [isDark]
  );

  const disabledInputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg ${
        isDark
          ? "bg-gray-700/20 border-gray-600 text-gray-400"
          : "bg-gray-100 border-gray-300 text-gray-600"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block text-sm font-medium mb-2 ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const pillClass = useMemo(
    () =>
      `inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
        isDark ? "bg-blue-900/60 text-blue-100" : "bg-blue-100 text-blue-800"
      }`,
    [isDark]
  );

  /* ---------------- helpers ---------------- */
  const canUse = useCallback((cond, msg) => {
    if (!cond) toast.error(msg);
    return !!cond;
  }, []);

  const setField = useCallback((name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  const resetFiltersOnly = useCallback(() => {
    setFilters((prev) => ({
      ...defaultFilters,
      vehicleMode: prev.vehicleMode,
    }));
  }, []);

  const buildListParams = useCallback(
    (override = {}) => ({
      branchId,
      branchType,
      loginUserId,

      vehicleCode: toStr(override.vehicleCode ?? filters.vehicleCode),
      vehicleNumber: toStr(override.vehicleNumber ?? filters.vehicleNumber),
      toBranchId: selectValue(override.transferTo ?? filters.transferTo),
      store: selectValue(override.store ?? filters.store),
      vehTypeIndex: selectValue(override.vehTypeIndex ?? filters.vehTypeIndex),
      month: selectValue(override.month ?? filters.month),
      year: selectValue(override.year ?? filters.year),
    }),
    [branchId, branchType, loginUserId, filters]
  );

  /* =========================================================
     API LOADERS
     ========================================================= */
  const loadDropdowns = useCallback(async () => {
    const { data } = await axios.get(`${API_BASE}/init`, {
      params: {
        branchId,
        branchType,
        loginUserId,
      },
      headers: commonHeaders,
    });

    if (!data?.success) {
      throw new Error(data?.message || "Failed to load initial dropdowns");
    }

    const branches = toList(data.branches).map((x) => ({
      label: String(x.label ?? "Select"),
      value: String(x.value ?? "Select"),
    }));

    const stores = toList(data.stores).map((x) => ({
      label: String(x.label ?? "Select"),
      value: String(x.value ?? "Select"),
    }));

    const vehicleTypes = toList(data.vehicleTypes).map((x) => ({
      label: String(x.label ?? x.fld_vehicletypename ?? "Select"),
      value: String(x.value ?? x.fld_vehicletypeindex ?? "Select"),
    }));

    const years = toList(data.years).map((y) => ({
      label: String(y),
      value: String(y),
    }));

    const months =
      toList(data.months).length > 0
        ? toList(data.months).map((m) => ({
            label: String(m),
            value: String(m),
          }))
        : MONTHS.map((m) => ({ label: m, value: m }));

    setOpts((prev) => ({
      ...prev,
      branches: branches.length
        ? branches
        : [{ label: "Select", value: "Select" }],
      stores: stores.length ? stores : [{ label: "Select", value: "Select" }],
      vehicleTypes: vehicleTypes.length
        ? vehicleTypes
        : [{ label: "Select", value: "Select" }],
      years: years.length ? years : [{ label: "Select", value: "Select" }],
      months: months.length
        ? months
        : MONTHS.map((m) => ({ label: m, value: m })),
    }));

    setTransferFromName(data.transferFromName || "");
  }, [branchId, branchType, loginUserId, commonHeaders]);

  const loadList = useCallback(
    async ({ silent = false, overrideFilters = null } = {}) => {
      if (!canUse(toInt(loginUserId), "loginUserId missing")) return;
      if (!canUse(toInt(branchId), "branchId missing")) return;

      try {
        setGridLoading(true);

        const endpoint =
          (overrideFilters?.vehicleMode ?? filters.vehicleMode) === "spares"
            ? "list/spares"
            : "list/own";

        const { data } = await axios.get(`${API_BASE}/${endpoint}`, {
          params: buildListParams(overrideFilters || {}),
          headers: commonHeaders,
        });

        if (!data?.success) {
          setRows([]);
          setPage(1);
          if (!silent) toast.warn(data?.message || "No records found");
          return;
        }

        const listRows = toList(data.list).map((r, idx) => ({
          __rowId: idx + 1,
          ...r,
        }));

        setRows(listRows);
        setPage(1);

        if (!silent) {
          toast.success(`Found ${listRows.length} record(s)`);
        }
      } catch (err) {
        console.error(err);
        setRows([]);
        setPage(1);
        toast.error(
          err?.response?.data?.message || err.message || "Failed to load list"
        );
      } finally {
        setGridLoading(false);
      }
    },
    [loginUserId, branchId, filters.vehicleMode, commonHeaders, buildListParams, canUse]
  );

  const loadInit = useCallback(async () => {
    if (!canUse(toInt(loginUserId), "loginUserId missing")) return;
    if (!canUse(toInt(branchId), "branchId missing")) return;

    try {
      setPageLoading(true);
      setDropdownLoading(true);
      await loadDropdowns();
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Initialization failed"
      );
    } finally {
      setDropdownLoading(false);
      setPageLoading(false);
    }
  }, [loginUserId, branchId, canUse, loadDropdowns]);

  /* ---------------- actions ---------------- */
  const handleSearch = useCallback(async () => {
    await loadList({ silent: false });
  }, [loadList]);

  const handleViewAll = useCallback(async () => {
    const resetObj = {
      ...defaultFilters,
      vehicleMode: filters.vehicleMode,
    };

    setFilters(resetObj);

    await loadList({
      silent: false,
      overrideFilters: resetObj,
    });
  }, [filters.vehicleMode, loadList]);

  const handleRefresh = useCallback(async () => {
    await loadList({ silent: false });
  }, [loadList]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  /* ---------------- effects ---------------- */
  useEffect(() => {
    loadInit();
  }, [loadInit]);

  const recordsCount = useMemo(() => rows.length, [rows.length]);

  const renderTableHead = () => {
    if (filters.vehicleMode === "spares") {
      return (
        <tr>
          <th className="px-4 py-4 text-center font-semibold">S.No</th>
          <th className="px-4 py-4 text-center font-semibold">Vehicle Main Type</th>
          <th className="px-4 py-4 text-center font-semibold">Spares Vehicle Code</th>
          <th className="px-4 py-4 text-center font-semibold">SL No</th>
          <th className="px-4 py-4 text-center font-semibold">Item Name</th>
          <th className="px-4 py-4 text-center font-semibold">From Branch</th>
          <th className="px-4 py-4 text-center font-semibold">To Branch</th>
          <th className="px-4 py-4 text-center font-semibold">Transfer Date</th>
          <th className="px-4 py-4 text-center font-semibold">Approved</th>
          <th className="px-4 py-4 text-center font-semibold">Received</th>
          <th className="px-4 py-4 text-center font-semibold">Action</th>
        </tr>
      );
    }

    return (
      <tr>
        <th className="px-4 py-4 text-center font-semibold">S.No</th>
        <th className="px-4 py-4 text-center font-semibold">Vehicle Code</th>
        <th className="px-4 py-4 text-center font-semibold">Vehicle Number</th>
        <th className="px-4 py-4 text-center font-semibold">Vehicle Name</th>
        <th className="px-4 py-4 text-center font-semibold">From Branch</th>
        <th className="px-4 py-4 text-center font-semibold">To Branch</th>
        <th className="px-4 py-4 text-center font-semibold">Transfer Date</th>
        <th className="px-4 py-4 text-center font-semibold">Approved Status</th>
        <th className="px-4 py-4 text-center font-semibold">Receive Status</th>
        <th className="px-4 py-4 text-center font-semibold">Action</th>
      </tr>
    );
  };

  const renderRow = (row, idx) => {
    const sno = (page - 1) * pageSize + idx + 1;

    if (filters.vehicleMode === "spares") {
      return (
        <tr
          key={row.fld_spare_trandferid ?? row.__rowId ?? sno}
          className={isDark ? "border-t border-gray-800" : "border-t border-gray-100"}
        >
          <td className="px-4 py-4 text-sm text-center">{sno}</td>
          <td className="px-4 py-4 text-sm text-center">
            {row.fld_vehicletypename || "-"}
          </td>
          <td className="px-4 py-4 text-sm text-center">
            {row.fld_sparesvehiclecode || "-"}
          </td>
          <td className="px-4 py-4 text-sm text-center">{row.fld_slno || "-"}</td>
          <td className="px-4 py-4 text-sm text-center">
            {row.fld_itemname || "-"}
          </td>
          <td className="px-4 py-4 text-sm text-center">
            {row.fld_frombranch || "-"}
          </td>
          <td className="px-4 py-4 text-sm text-center">
            {row.fld_tobranch || "-"}
          </td>
          <td className="px-4 py-4 text-sm text-center">
            {row.fld_TransferDate || "-"}
          </td>
          <td className="px-4 py-4 text-sm text-center">
            <span
              className={
                row.approvedstatus === "Approved"
                  ? "text-green-600"
                  : "text-yellow-600"
              }
            >
              {row.approvedstatus || "-"}
            </span>
          </td>
          <td className="px-4 py-4 text-sm text-center">
            <span
              className={
                row.Rectatus === "Received"
                  ? "text-green-600"
                  : "text-yellow-600"
              }
            >
              {row.Rectatus || "-"}
            </span>
          </td>
          <td className="px-4 py-3 text-center">
            <button
              onClick={handleRefresh}
              disabled={isBusy}
              className="inline-flex items-center gap-1 px-3 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition-all duration-200 disabled:opacity-60"
            >
              <FaSyncAlt className="w-3 h-3" />
              Refresh
            </button>
          </td>
        </tr>
      );
    }

    return (
      <tr
        key={`${row.fld_vehicle_code || row.__rowId || ""}-${sno}`}
        className={isDark ? "border-t border-gray-800" : "border-t border-gray-100"}
      >
        <td className="px-4 py-4 text-sm text-center">{sno}</td>
        <td className="px-4 py-4 text-sm text-center">
          {row.fld_vehicle_code || "-"}
        </td>
        <td className="px-4 py-4 text-sm text-center">
          {row.fld_vehicle_regno || "-"}
        </td>
        <td className="px-4 py-4 text-sm text-center">
          {row.fld_vehicle_name || "-"}
        </td>
        <td className="px-4 py-4 text-sm text-center">
          {row.frombranchname || "-"}
        </td>
        <td className="px-4 py-4 text-sm text-center">
          {row.tobranchname || "-"}
        </td>
        <td className="px-4 py-4 text-sm text-center">
          {row.fld_transferdate || "-"}
        </td>
        <td className="px-4 py-4 text-sm text-center">
          <span
            className={
              row.approvedstatus === "Approved"
                ? "text-green-600"
                : "text-yellow-600"
            }
          >
            {row.approvedstatus || "-"}
          </span>
        </td>
        <td className="px-4 py-4 text-sm text-center">
          <span
            className={
              row.Rectatus === "Received"
                ? "text-green-600"
                : "text-yellow-600"
            }
          >
            {row.Rectatus || "-"}
          </span>
        </td>
        <td className="px-4 py-3 text-center">
          <button
            onClick={handleRefresh}
            disabled={isBusy}
            className="inline-flex items-center gap-1 px-3 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition-all duration-200 disabled:opacity-60"
          >
            <FaSyncAlt className="w-3 h-3" />
            Refresh
          </button>
        </td>
      </tr>
    );
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              VEHICLE TRANSFER STATUS
            </span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <div className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Transferred Vehicle Current Status
              </div>

              <div className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch ID: {branchId || "-"} {" • "} User ID: {loginUserId || "-"}
              </div>

              <div className="mt-2 flex flex-wrap gap-2">
                <span className={pillClass}>Records: {recordsCount}</span>
                <span className={pillClass}>
                  Vehicle Mode: {filters.vehicleMode === "own" ? "Own Vehicle" : "Spares & Catalogue"}
                </span>
                <span className={pillClass}>
                  Branch Type: {branchType || "-"}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowFilters((s) => !s)}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleRefresh}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>

              <button
                onClick={handleBack}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {/* Mode Section */}
          <div className={sectionClass}>
            <div className="mb-6">
              <label className={labelClass}>Vehicle Type</label>
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    value="own"
                    checked={filters.vehicleMode === "own"}
                    onChange={(e) => setField("vehicleMode", e.target.value)}
                    className="form-radio text-blue-600"
                    disabled={isBusy}
                  />
                  <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                    Own Vehicle
                  </span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    value="spares"
                    checked={filters.vehicleMode === "spares"}
                    onChange={(e) => setField("vehicleMode", e.target.value)}
                    className="form-radio text-blue-600"
                    disabled={isBusy}
                  />
                  <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                    Spares & Catalogue
                  </span>
                </label>
              </div>
            </div>

            {/* Filters */}
            {showFilters && (
              <div className={sectionClass}>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <FaFilter /> FILTRATION AREA
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className={labelClass}>Vehicle Code</label>
                    <input
                      type="text"
                      className={inputClass}
                      placeholder="Enter vehicle code"
                      value={filters.vehicleCode}
                      onChange={(e) => setField("vehicleCode", e.target.value)}
                      disabled={isBusy || filters.vehicleMode === "spares"}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Vehicle Number</label>
                    <input
                      type="text"
                      className={inputClass}
                      placeholder={
                        filters.vehicleMode === "spares"
                          ? "Enter SL No"
                          : "Enter vehicle number"
                      }
                      value={filters.vehicleNumber}
                      onChange={(e) => setField("vehicleNumber", e.target.value)}
                      disabled={isBusy || filters.vehicleMode === "spares"}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Transfer From</label>
                    <input
                      type="text"
                      value={transferFromName || `Branch ${branchId}`}
                      disabled
                      className={disabledInputClass}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Transfer To</label>
                    <select
                      className={inputClass}
                      value={filters.transferTo}
                      onChange={(e) => setField("transferTo", e.target.value)}
                      disabled={isBusy}
                    >
                      {opts.branches.map((o, i) => (
                        <option key={i} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Store</label>
                    <select
                      className={inputClass}
                      value={filters.store}
                      onChange={(e) => setField("store", e.target.value)}
                      disabled={isBusy}
                    >
                      {opts.stores.map((o, i) => (
                        <option key={i} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Vehicle Main Type</label>
                    <select
                      className={inputClass}
                      value={filters.vehTypeIndex}
                      onChange={(e) => setField("vehTypeIndex", e.target.value)}
                      disabled={isBusy}
                    >
                      {opts.vehicleTypes.map((o, i) => (
                        <option key={i} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Month</label>
                    <select
                      className={inputClass}
                      value={filters.month}
                      onChange={(e) => setField("month", e.target.value)}
                      disabled={isBusy}
                    >
                      {opts.months.map((o, i) => (
                        <option key={i} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Year</label>
                    <select
                      className={inputClass}
                      value={filters.year}
                      onChange={(e) => setField("year", e.target.value)}
                      disabled={isBusy}
                    >
                      {opts.years.map((o, i) => (
                        <option key={i} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* action buttons */}
                <div className="flex flex-col md:flex-row gap-3 justify-between items-center mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={resetFiltersOnly}
                    disabled={isBusy}
                    className={`flex items-center gap-2 ${
                      isDark
                        ? "text-gray-300 hover:text-white"
                        : "text-gray-600 hover:text-gray-900"
                    } font-medium transition-colors duration-200 ${
                      isBusy ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    <FaUndo />
                    Clear Filters
                  </button>

                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={handleSearch}
                      disabled={isBusy}
                      className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60`}
                    >
                      <FaSearch />
                      {gridLoading ? "Searching..." : "Search"}
                    </button>

                    <button
                      onClick={handleViewAll}
                      disabled={isBusy}
                      className="flex items-center gap-2 px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-60"
                    >
                      <FaEye />
                      View All
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* GRID */}
          <div className="mt-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3">
              <div className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-800"}`}>
                Vehicle Transfer Status List
              </div>
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Vehicle Mode:{" "}
                <span className="font-semibold">
                  {filters.vehicleMode === "own" ? "Own Vehicle" : "Spares & Catalogue"}
                </span>
                {" • "}
                Records: <span className="font-semibold">{rows.length}</span>
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
              <table className="min-w-full text-sm">
                <thead className={`${buttonClass} text-white`}>
                  {renderTableHead()}
                </thead>

                <tbody className={isDark ? "bg-gray-900 text-gray-100" : "bg-white text-gray-800"}>
                  {rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={filters.vehicleMode === "spares" ? 11 : 10}
                        className="px-4 py-10 text-center text-gray-500"
                      >
                        {gridLoading || pageLoading ? "Loading..." : "No records found"}
                      </td>
                    </tr>
                  ) : (
                    paginatedRows.map((row, idx) => renderRow(row, idx))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {rows.length > 0 && (
              <div className="flex justify-center mt-6">
                <nav className="flex items-center space-x-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1 || isBusy}
                    className={`px-3 py-1 border rounded ${
                      isDark
                        ? "bg-gray-800 text-white border-gray-700"
                        : "bg-white text-gray-700 border-gray-300"
                    } disabled:opacity-50`}
                  >
                    Prev
                  </button>

                  {Array.from({ length: totalPages }).map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setPage(i + 1)}
                      disabled={isBusy}
                      className={`px-3 py-1 border rounded ${
                        page === i + 1
                          ? `${buttonClass} text-white`
                          : isDark
                          ? "bg-gray-800 text-white border-gray-700"
                          : "bg-white text-gray-700 border-gray-300"
                      } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                    >
                      {i + 1}
                    </button>
                  ))}

                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages || isBusy}
                    className={`px-3 py-1 border rounded ${
                      isDark
                        ? "bg-gray-800 text-white border-gray-700"
                        : "bg-white text-gray-700 border-gray-300"
                    } disabled:opacity-50`}
                  >
                    Next
                  </button>
                </nav>
              </div>
            )}

            {isBusy && (
              <div className={`mt-3 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                {pageLoading
                  ? "Loading page..."
                  : dropdownLoading
                  ? "Loading dropdowns..."
                  : gridLoading
                  ? "Loading grid..."
                  : "Loading..."}
              </div>
            )}
          </div>

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default TransferredVehicleStatus;